import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { FormValues } from './types.d'
import { useDispatch } from 'react-redux'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { OnboardingState } from 'app/auth/components/hooks/use-onboarding-setup'
import { OnboardingStepKey } from '@cbgms/api'
import { PaymentMethodsForm } from './PaymentMethodsForm'
import { createPaymentMethod } from 'app/settings/state/PaymentMethods/actions'
import { PaymentMethodsCTA } from './PaymentMethodsCTA'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'

interface StepInputProps {
  basePath: string
}

interface StepProps {
  basePath: string
  onSubmit: (v: FormValues) => void
  onboardingInfo: OnboardingState
}

export const usePaymentMethodsStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, onboardingInfo }) => {
  const [paymentMethod, setPaymentMethod] = useState<FormValues | null>(null)

  const dispatch = useDispatch()
  const stepName = OnboardingStepKey.PaymentMethods
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const onStepSubmit = (values: FormValues) => {
    setPaymentMethod(values)
  }

  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    if (paymentMethod) {
      await dispatch(createPaymentMethod(paymentMethod))
    }
  }, [paymentMethod, dispatch])

  return {
    Component: PaymentMethodsStep,
    CTAComponent: PaymentMethodsCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: false,
    showStepperHeader: false,
    hideSkip: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    stepProps: { duplicationCheck: stepData.DuplicationCheck || [], basePath, onSubmit: complete.onSubmit, onboardingInfo },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.title_payment_methods')}</>
}

export const PaymentMethodsStep: React.FC<StepProps & defaultStepFormProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathPaymentMethodsStep(basePath)} render={() => <PaymentMethodsForm {...rest} />} />
}
